import { isSumerType } from '@1delta/lender-registry'
import type { BalanceData, AprData, LenderConfigMap, PostTradeMetrics } from './utils'
import type { LenderYields } from '../../../types/lenderTypes'
import type { SumerPositionInput } from './sumer/types'
import { computeWithdrawDelta as computeStandardWithdrawDelta } from './standard/withdraw'
import { computeSumerWithdrawDelta } from './sumer/withdraw'

/**
 * Unified withdraw operation for all lending protocols.
 *
 * Routes to the appropriate implementation based on the lender type:
 * - Sumer: Uses waterfall absorption model
 * - Standard: Uses standard collateral factor model (Aave, Compound, Venus, etc.)
 *
 * @param lender - The lender protocol identifier (e.g., 'AAVE_V3', 'SUMER')
 * @param amount - The token amount to withdraw
 * @param price - The USD price of the token
 * @param balanceData - Current balance state
 * @param config - (Standard only) Lender configuration map
 * @param modeId - (Standard only) Mode/category ID
 * @param targetMarketUid - (Sumer only) Target market UID
 * @param positions - (Sumer only) Array of all Sumer positions
 * @param apr - Optional current APR data
 * @param yieldParams - Optional yield parameters for APR calculation
 */
export function computeWithdrawDelta(
  lender: string,
  amount: number,
  price: number,
  balanceData: BalanceData,
  config?: LenderConfigMap,
  modeId?: string,
  apr?: AprData,
  yieldParams?: LenderYields,
  // Sumer variant params (at the end for backward compatibility)
  targetMarketUid?: string,
  positions?: SumerPositionInput[],
): PostTradeMetrics {
  if (isSumerType(lender)) {
    if (!positions || targetMarketUid === undefined) {
      throw new Error('Sumer operations require positions and targetMarketUid')
    }
    return computeSumerWithdrawDelta(
      amount,
      price,
      targetMarketUid,
      balanceData,
      positions,
      apr,
      yieldParams,
    )
  }

  // Standard variant (all other protocols)
  if (!config || modeId === undefined) {
    throw new Error('Standard operations require config and modeId')
  }
  return computeStandardWithdrawDelta(
    amount,
    price,
    balanceData,
    config,
    modeId,
    apr,
    yieldParams,
  )
}
